import { useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import Refresh from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid-premium";
import {
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
  DocumentDialog,
  EditMode,
  FormState,
  SimilarityCell,
} from "components/Document";
import { DocumentDetailDrawer } from "components/Document/DocumentDetailDrawer";
import { FileTypeIcon } from "components/Document/shared/fileTypeUtils";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TagsDisplay } from "components/TagsDisplay";
import { gql } from "generated-graphql";
import {
  DocumentQuery,
  DocumentsQuery,
  DocumentsQueryVariables,
  DocumentType,
  ExtractionStatus,
  JobStatus,
  TierIiReportEncampStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentAI } from "hooks/useDocumentAI";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { OmnisearchGridColDef, useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { client } from "providers/apollo";
import { useAnalyzeDocuments } from "providers/documentAIAnalysis";
import { useDocumentReview } from "providers/documentReview";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { documentTypeToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";
import {
  DocumentReviewStatus,
  FILTERABLE_DOCUMENT_REVIEW_STATUSES,
  getDocumentReviewStatusLabel,
  getDocumentReviewStatusLabelFromRow,
} from "util/documentReviewStatus";

import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { DocumentsBulkActionsMenu } from "./DocumentsBulkActionsMenu";
import { useRetryDocumentExtraction } from "./hooks/useRetryDocumentExtraction";

export type Row = DocumentsQuery["documents"]["items"][number];

export const DOCUMENT_FRAGMENT = gql(`
  fragment DocumentFields on Document {
    id
    tenantId
    title
    documentType
    description
    fileExtension
    storageLink
    createdAt
    authoredAt
    extractionStatus
    currentSubmissionDocument
    sizeMegabytes
    report {
      encampStatus
    }
    facilities {
      id
      name
    }
    documentTags {
      name
      id
    }
    chemicalDocument {
      chemical {
        name
        id
      }
    }
    job {
      id
      status
      task
    }
    documentMetadata {
      needsReview
    }
    associatedReports {
      reportId
      submissionId
      reportingYear
      programArea
    }
  }
`);

export const DOCUMENTS = gql(`
  query Documents($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    documents(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...DocumentFields
      }
      count
      similarities {
        documentId
        similarity
      }
    }
  }
`);

export const DOCUMENT = gql(`
  query Document($id: ID!) {
    document(id: $id) {
      ...DocumentFields
    }
  }
`);

export function Documents() {
  const { tenantId, tenant } = useTenant();
  const { isStaff } = useCurrentUser();
  const { job } = useAnalyzeDocuments();
  const { documentsToReview, setDocumentsToReview, clearDocumentsToReview } = useDocumentReview();
  const hasDocumentAI = useDocumentAI();
  const [selectedDocument, setSelectedDocument] = useState<Row | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  const [drawerDocument, setDrawerDocument] = useState<Row | undefined>(undefined);

  const persistenceKey = "documents-table";

  const { setOmnisearch } = useOmnisearchDatagrid({
    persistenceKey,
  });

  // Track similarity scores from semantic search results
  const similarityMapRef = useRef<Map<string, number> | null>(null);
  const [hasSemanticResults, setHasSemanticResults] = useState(false);

  const handleDataStateChange = useCallback((data: DocumentsQuery | undefined) => {
    const similarities = data?.documents?.similarities;
    if (similarities && similarities.length > 0) {
      similarityMapRef.current = new Map(similarities.map((s) => [s.documentId, s.similarity]));
      setHasSemanticResults(true);
    } else {
      similarityMapRef.current = null;
      setHasSemanticResults(false);
    }
  }, []);

  // Use the hook for managing document ID in URL
  const {
    selectedId: documentId,
    pushIdToUrl,
    setSelectedId,
    popIdFromUrl,
  } = useSelectedIdInUrl("documentId");

  // Push first document ID to URL when documentsToReview is set
  useEffect(() => {
    if (documentsToReview.length > 0 && !documentId) {
      pushIdToUrl(documentsToReview[0]);
    }
  }, [documentsToReview, documentId, pushIdToUrl]);

  // Determine which documents to show: bulk list from provider or single from URL
  const documentsToReviewList = useMemo(() => {
    if (!hasDocumentAI) {
      return [];
    }

    if (documentsToReview.length > 0) {
      return documentsToReview;
    }

    if (documentId) {
      return [documentId];
    }

    return [];
  }, [documentsToReview, documentId, hasDocumentAI]);

  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [addEditDialogState, setAddEditDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );

  useBreadcrumb([{ label: "Documents" }]);

  const { fetchAndHandleDownload } = useDocumentDownload();
  const apiRef = useGridApiRef();

  const {
    retryDocumentExtractions,
    result: { loading: retryLoading },
  } = useRetryDocumentExtraction();

  const handleRetryDocumentExtractions = useCallback(
    async (document: Row) => {
      if (!document.tenantId) {
        return;
      }

      await retryDocumentExtractions(document.tenantId, [document.id]);
    },
    [retryDocumentExtractions]
  );

  const searchByFacility = useCallback(
    (facilityName: string) => {
      setOmnisearch(
        /\s/.test(facilityName) ? `facility:"${facilityName}"` : `facility:${facilityName}`
      );
    },
    [setOmnisearch]
  );

  // Clear documents to review when URL is cleared (but preserve if documentsToReview from provider)
  useEffect(() => {
    if (!documentId && documentsToReview.length === 0) {
      clearDocumentsToReview();
    }
  }, [documentId, documentsToReview.length, clearDocumentsToReview]);

  const cachedDocumentForEdit = useMemo(() => {
    if (!documentId) {
      return undefined;
    }

    const cacheId = client.cache.identify({
      __typename: "Document",
      id: documentId,
    });

    if (!cacheId) {
      return undefined;
    }

    const cached = client.cache.readFragment<Row>({
      id: cacheId,
      fragment: DOCUMENT_FRAGMENT,
      fragmentName: "DocumentFields",
    });
    return cached ?? undefined;
  }, [documentId]);

  const { data: fetchedDocumentData } = useQuery<DocumentQuery>(DOCUMENT, {
    skip: hasDocumentAI || !documentId || !!cachedDocumentForEdit,
    variables: { id: documentId ?? "" },
  });

  const documentForEdit = cachedDocumentForEdit ?? fetchedDocumentData?.document;

  const handleClickChemicalLink = useCallback(
    (params: { row: Row }) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (params.row.chemicalDocument?.length) {
        e.preventDefault();
        e.stopPropagation();
        const chemicalDocument = params.row.chemicalDocument[0];
        const chemical = chemicalDocument.chemical;
        const link = `/o/${tenantId}/chemicals/catalog/chemicals/${chemical.id}`;
        window.open(link, "_blank", "noopener,noreferrer");
      }
    },
    [tenantId]
  );

  const buildDialogStateFromDocument = useCallback(
    (document: Row): AddEditDocumentDialogState => ({
      ...defaultAddEditDocumentDialogState,
      open: true,
      mode: EditMode.EDIT,
      step: FormState.FORM,
      documentId: document.id,
      title: document.title ?? "",
      documentType: document.documentType ?? DocumentType.Other,
      storageLink: document.storageLink ?? undefined,
      fileExtension: document.fileExtension ?? undefined,
      facilities:
        document.facilities?.map((facility) => ({
          id: facility.id,
          name: facility.name ?? "",
        })) ?? [],
      documentTags:
        document.documentTags?.map((tag) => ({
          id: tag.id,
          name: tag.name,
        })) ?? [],
      authoredAt: document.authoredAt ?? undefined,
      description: document.description ?? undefined,
    }),
    []
  );

  const openEditDialog = useCallback(
    (row: Row) => {
      pushIdToUrl(row.id);
      if (!hasDocumentAI) {
        setAddEditDialogState(buildDialogStateFromDocument(row));
      }
    },
    [pushIdToUrl, setAddEditDialogState, buildDialogStateFromDocument, hasDocumentAI]
  );

  useEffect(() => {
    if (hasDocumentAI || !documentId || !documentForEdit) {
      return;
    }

    setAddEditDialogState((state) => {
      if (state.open && state.documentId === documentForEdit.id) {
        return state;
      }

      return buildDialogStateFromDocument(documentForEdit);
    });
  }, [
    documentId,
    documentForEdit,
    buildDialogStateFromDocument,
    setAddEditDialogState,
    hasDocumentAI,
  ]);

  const MAX_VISIBLE_YEARS = 3;

  // Grid Column Definitions
  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "title",
        headerName: "Name",
        flex: 0.75,
        sortable: true,
        renderCell(params) {
          return (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <FileTypeIcon ext={params.row.fileExtension} />
              <Typography variant="body2" noWrap>
                {params.row.title}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "documentType",
        headerName: "Document Type",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          return documentTypeToLabel(row.documentType);
        },
        renderCell({ value }) {
          return (
            <Typography variant="body2" noWrap>
              {value}
            </Typography>
          );
        },
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(DocumentType),
        enumPresentationFunction: (enumValue) => documentTypeToLabel(enumValue as DocumentType),
      },
      {
        field: "facility",
        headerName: "Facility",
        filterKeyType: "facility",
        flex: 0.5,
        sortable: false,
        renderCell(params) {
          const facilities = params.row.facilities ?? [];
          if (facilities.length === 0) return null;

          if (facilities.length === 1) {
            return (
              <Typography
                variant="body2"
                noWrap
                sx={{
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  searchByFacility(facilities[0].name);
                }}
              >
                {facilities[0].name}
              </Typography>
            );
          }

          const sorted = [...facilities].sort((a, b) => a.name.localeCompare(b.name));
          const MAX_TOOLTIP_FACILITIES = 25;
          const visible = sorted.slice(0, MAX_TOOLTIP_FACILITIES);
          const overflow = sorted.length - MAX_TOOLTIP_FACILITIES;

          return (
            <Tooltip
              title={
                <Box>
                  {visible.map((f) => (
                    <div key={f.id}>{f.name}</div>
                  ))}
                  {overflow > 0 && <div>{`+ ${overflow} more`}</div>}
                </Box>
              }
            >
              <Chip
                label={`${facilities.length} facilities`}
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        field: "chemicalDocument",
        headerName: "Chemical",
        flex: 0.4,
        sortable: false,
        renderCell(params) {
          const name = params.row.chemicalDocument?.[0]?.chemical?.name;
          if (!name) return null;
          return (
            <Box
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              <Link target="_blank" onClick={handleClickChemicalLink({ row: params.row })}>
                {name}
              </Link>
            </Box>
          );
        },
      },
      {
        field: "reportingYear",
        headerName: "Reporting Years",
        flex: 0.5,
        sortable: false,
        renderCell(params) {
          const reports = params.row.associatedReports ?? [];
          const years = [...new Set(reports.map((r) => r.reportingYear))].sort((a, b) => b - a);
          if (years.length === 0) return null;

          const visible = years.slice(0, MAX_VISIBLE_YEARS);
          const overflow = years.length - MAX_VISIBLE_YEARS;

          return (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {visible.map((year) => {
                const href = `/o/${tenantId}/chemicals/reports?omnisearch=reportingYear:${year}`;
                return (
                  <Chip
                    key={year}
                    label={year}
                    size="small"
                    color="primary"
                    variant="outlined"
                    component="a"
                    href={href}
                    clickable
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                );
              })}
              {overflow > 0 && (
                <Tooltip title={years.slice(MAX_VISIBLE_YEARS).join(", ")}>
                  <Chip label={`+${overflow}`} size="small" variant="outlined" />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Uploaded On",
        flex: 0.25,
        sortable: true,
        valueGetter(_, row) {
          return prettyPrintDateMed(row.createdAt);
        },
      },
      ...(hasDocumentAI
        ? [
            {
              field: "reviewStatus",
              headerName: "Review Status",
              flex: 0.4,
              sortable: true,
              filterable: hasDocumentAI,
              filterKeyType: "enumAutocomplete",
              enumValues: FILTERABLE_DOCUMENT_REVIEW_STATUSES,
              enumPresentationFunction: (enumValue: string) =>
                getDocumentReviewStatusLabel(enumValue as DocumentReviewStatus),
              valueGetter(_: unknown, row: Row) {
                return getDocumentReviewStatusLabelFromRow(row);
              },
              renderCell(params: { row: Row }) {
                const status = getDocumentReviewStatusLabelFromRow(params.row);
                return (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="body2" noWrap>
                      {status}
                    </Typography>
                  </Box>
                );
              },
            } satisfies OmnisearchGridColDef<Row>,
          ]
        : []),
      ...(hasSemanticResults && isStaff
        ? [
            {
              field: "similarity",
              headerName: "Relevance",
              width: 120,
              sortable: false,
              filterable: false,
              resizable: false,
              valueGetter(_: unknown, row: Row) {
                const similarity = similarityMapRef.current?.get(row.id);
                return similarity != null ? Math.round(similarity * 100) : null;
              },
              renderCell(params) {
                return <SimilarityCell value={params.value as number | null} />;
              },
            } as OmnisearchGridColDef<Row>,
          ]
        : []),
      {
        field: "tags",
        headerName: "Tags",
        flex: 0.25,
        filterKeyType: "documentTags",
        renderCell: ({ row }) => (
          <TagsDisplay tags={row.documentTags?.map((t) => ({ id: t.id, name: t.name })) ?? []} />
        ),
      },
      {
        field: "actions",
        type: "actions",
        width: 125,
        align: "left",
        resizable: false,
        getActions: ({ row }: { row: Row }) => {
          const actions = [
            <GridActionsCellItem
              key="edit-document"
              onClick={() => openEditDialog(row)}
              label="Edit Document"
              icon={<Edit />}
              disabled={row.extractionStatus === ExtractionStatus.Pending}
            />,
            <GridActionsCellItem
              key="download-document"
              onClick={async () =>
                await fetchAndHandleDownload({
                  documentId: row.id,
                })
              }
              label="Download Document"
              icon={<Download />}
              disabled={row.extractionStatus === ExtractionStatus.Pending}
            />,
          ];

          if (hasDocumentAI) {
            actions.push(
              <GridActionsCellItem
                key="retry-extraction"
                showInMenu={true}
                onClick={async () => {
                  await handleRetryDocumentExtractions(row);
                }}
                label="Retry Extraction"
                icon={<Refresh />}
                disabled={
                  row.extractionStatus === ExtractionStatus.Pending || retryLoading || !row.tenantId
                }
              />
            );
          }

          if (
            row.report == null ||
            row.report.encampStatus !== TierIiReportEncampStatus.FilingComplete
          ) {
            actions.push(
              <GridActionsCellItem
                key="remove-document"
                onClick={() => {
                  setSelectedDocument(row);
                  setShowConfirmDeleteDialog(true);
                }}
                label="Remove Document"
                showInMenu={hasDocumentAI}
                icon={<Delete />}
                disabled={row.extractionStatus === ExtractionStatus.Pending}
              />
            );
          }

          return actions;
        },
      },
    ],
    [
      hasDocumentAI,
      hasSemanticResults,
      isStaff,
      searchByFacility,
      fetchAndHandleDownload,
      handleClickChemicalLink,
      openEditDialog,
      handleRetryDocumentExtractions,
      retryLoading,
      tenantId,
    ]
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const mobileColumnVisibility = isMobile
    ? {
        chemicalDocument: false,
        reportingYear: false,
        createdAt: false,
        ...(hasDocumentAI && { reviewStatus: false }),
      }
    : undefined;

  return (
    <Box sx={{ pt: 3 }}>
      <ConfirmDeleteDialog
        open={showConfirmDeleteDialog}
        selectedDocument={selectedDocument}
        onClose={() => {
          setShowConfirmDeleteDialog(false);
          setSelectedDocument(undefined);
        }}
      />
      <OmnisearchDataGrid<
        DocumentsQuery,
        DocumentsQueryVariables,
        DocumentsQuery["documents"]["items"][number]
      >
        persistenceKey={persistenceKey}
        isURLDriven
        apiRef={apiRef}
        onSelectedRowsChanged={setSelectedRows}
        columns={columns}
        dataQuery={DOCUMENTS}
        isRowSelectable={(params) =>
          !!params.row && params.row.extractionStatus !== ExtractionStatus.Pending
        }
        disableRowSelectionOnClick={true}
        initialSortModel={[
          { field: "reviewStatus", sort: "asc" },
          { field: "createdAt", sort: "desc" },
        ]}
        defaultSearch={`tenantId:${tenantId}`}
        excludeFilterColumns={["createdAt"]}
        getItems={(data) => data.documents.items}
        getCount={(data) => data.documents.count ?? 0}
        onDataStateChange={handleDataStateChange}
        noDataMessage="No documents have been added to this organization yet."
        onRowClick={({ row }) => {
          if (row.extractionStatus !== ExtractionStatus.Pending) {
            setDrawerDocument(row);
          }
        }}
        initialState={{}}
        commandButtons={[
          <Button
            disabled={
              job?.status === JobStatus.Queued ||
              job?.status === JobStatus.Dispatched ||
              job?.status === JobStatus.Running
            }
            key="add-document-button"
            variant="contained"
            onClick={() => {
              setAddEditDialogState((state) => ({
                ...state,
                open: true,
                mode: EditMode.ADD,
              }));
            }}
          >
            Add Document
          </Button>,
          <DocumentsBulkActionsMenu
            key={"documents-bulk-actions-menu"}
            documents={selectedRows}
            apiRef={apiRef}
            hasDocumentAI={hasDocumentAI}
            onReviewMetadata={(documentIds) => {
              setDocumentsToReview(documentIds);
              pushIdToUrl(documentIds[0]);
            }}
          />,
        ]}
        rowHeight="auto"
        initialPageSize={50}
        columnVisibilityModel={mobileColumnVisibility}
      />

      <DocumentDetailDrawer
        document={drawerDocument}
        open={!!drawerDocument}
        onClose={() => setDrawerDocument(undefined)}
        onEdit={(doc) => {
          setDrawerDocument(undefined);
          openEditDialog(doc);
        }}
        tenantId={tenantId}
        tenantName={tenant?.name}
      />

      {tenantId && (
        <DocumentDialog
          tenantId={tenantId}
          reviewDialogProps={{
            open: documentsToReviewList.length > 0,
            onClose: () => {
              clearDocumentsToReview();
              popIdFromUrl();
              setAddEditDialogState(defaultAddEditDocumentDialogState);
            },
            documentIds: documentsToReviewList,
            onComplete: () => {
              client.refetchQueries({ include: [DOCUMENTS] });
              clearDocumentsToReview();
              popIdFromUrl();
              setAddEditDialogState(defaultAddEditDocumentDialogState);
            },
            onCurrentDocumentChange: setSelectedId,
          }}
          addEditDialogProps={{
            showFacilityPicker: true,
            showTagPicker: true,
            onSubmit: () => {
              client.refetchQueries({ include: [DOCUMENTS] });
            },
            refetchQueries: [DOCUMENTS],
            dialogState: addEditDialogState,
            setDialogState: setAddEditDialogState,
          }}
        />
      )}
    </Box>
  );
}
